import { unvoidEncounterReview, voidEncounterReview } from "@/api/reviews";
import { ScoreDisplay } from "@/ai-coding-outputs/ScoreDisplay";
import { historicCodeSet } from "@/ai-coding-outputs/inlineCodeFlags";
import { useApprovedResultsFilters } from "@/ai-coding-outputs/admin-coding/useApprovedResultsFilters";
import { ROUTES } from "@/ai-coding-outputs/navigation";
import { useAuth } from "@/auth/AuthProvider";
import { USER_ROLES } from "@/auth/userRoles";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/ConfirmationDialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useResizableColumn } from "@/components/ui/useResizableColumn";
import { ResizeHandle } from "@/components/ui/ResizeHandle";
import { getOpcsVersionForDate, getOpcsVersionLabel } from "@/clinical-codes/opcsVersion";
import { getReviewPageBackground } from "@/customers/reviewPageColours";
import { useCustomerChangeAlert } from "@/customers/useCustomerChangeAlert";
import { usePageHeaderSlot } from "@/layout/PageHeaderSlotContext";
import { useProcessedFiles } from "@/inbound-data/useProcessedFiles";
import { supabase } from "@/integrations/supabase/client";
import { type OwnWriteBack, readDeliveryChain, resolveOwnWriteBack } from "@/write-back/deliveryChain";
import { useUserProfiles } from "@/user/useUserProfiles";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { ArrowRight, Filter, RotateCcw } from "lucide-react";
import React, { useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { countTimelineQuotes } from "../evidenceUtils";
import { DiagnosisTimeline } from "../human-coding/DiagnosisTimeline";
import { useDiagnosisEvidence } from "../human-coding/useDiagnosisEvidence";
import ProcessedFilesTabs from "../ProcessedFilesTabs";
import { TrustRulesButton } from "../TrustRulesButton";
import { useEvidenceHighlighting } from "../useEvidenceHighlighting";
import { useIndicationVerdicts } from "../useIndicationVerdicts";
import { useUnmatchedLeadTerms } from "../human-coding/useUnmatchedLeadTerms";
import { useSkipEncountersFilter } from "../useSkipEncountersFilter";
import AiResultsColumn from "./AiResultsColumn";
import { AcceptResultDialog } from "./AcceptResultDialog";
import { classificationResultQueryKey } from "./classificationResultQueryKey";
import { findRemovedCodes } from "@/ai-coding-outputs/admin-coding/codeComparison";
import HumanReviewColumn from "./HumanReviewColumn";
import { isGoldenDatasetSupported } from "./isGoldenDatasetSupported";
import { isManuallyApproved } from "./isManuallyApproved";
import { useAcceptClassificationResult } from "./useAcceptClassificationResult";
import { isReviewVoided, pickFirstNonVoidedReviewId } from "./humanReviewSelection";
import { OpenRawFilesButton } from "@/raw-files-viewer/OpenRawFilesButton";
import { InvestigatorPanel } from "./InvestigatorPanel";
import { getMastraWorkflowRunUrl } from "@/ai-coding-outputs/mastraWorkflowRunUrl";
import { ViewRunDropdown } from "@/ai-coding-outputs/ViewRunDropdown";
import { ApprovedResultsFilterPopover } from "./ApprovedResultsFilterPopover";
import { useNextUnwrittenEncounter } from "./useNextUnwrittenEncounter";
import { decisionLogFolderUrl } from "./decisionLogFolderUrl";
import { EncounterToolsMenu } from "./EncounterToolsMenu";
import { ReprocessSplitButton } from "./ReprocessSplitButton";
import { useProcessEncounter } from "./useProcessEncounter";
import { useRerunWithDecisionLog } from "./useRerunWithDecisionLog";
import { useReviewersFilter } from "./useReviewersFilter";
import { useWriteBackActions } from "./useWriteBackActions";

const writeBackLabel = (writeBack: Pick<OwnWriteBack<unknown>, "isSuperseded" | "isWrittenBack"> | undefined) => {
  if (writeBack?.isSuperseded) return "(superseded)";
  if (writeBack?.isWrittenBack) return "(written back)";
  return "(not written back)";
};

const UnwrittenEncounterReview = () => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { role } = useAuth();
  const {
    customerIds: filterCustomerIds,
    fromDate,
    setFilters,
    clearFilters,
    isCustomised,
  } = useApprovedResultsFilters();

  const [focusedClassificationResultId, setFocusedClassificationResultId] = React.useState<string | null>(null);
  const leftColumn = useResizableColumn({
    initial: 50,
    min: 30,
    max: 70,
    storageKey: "reviewed-result-column-width",
    unit: "percent",
  });
  const aiColumn = useResizableColumn({
    initial: 360,
    min: 280,
    max: 700,
    storageKey: "ai-column-width",
    unit: "pixel",
  });
  const [isRejectDialogOpen, setIsRejectDialogOpen] = React.useState<boolean>(false);
  const [isAcceptDialogOpen, setIsAcceptDialogOpen] = React.useState<boolean>(false);
  const [isInvestigatorOpen, setIsInvestigatorOpen] = React.useState<boolean>(false);
  const [voidAction, setVoidAction] = React.useState<{
    type: "unvoid";
    humanReviewId: string;
  } | null>(null);
  const [isVoidingReview, setIsVoidingReview] = React.useState<boolean>(false);
  const { hiddenReviewerIds, toggleReviewerVisibility } = useReviewersFilter();
  const pageHeaderSlot = usePageHeaderSlot();

  const canVoidUnvoidReview = role === USER_ROLES.admin || role === USER_ROLES.clinical_coder_admin;

  // Skip encounters filter
  const { skipEncounterIds, addSkipEncounterId, clearSkipEncounterIds } = useSkipEncountersFilter();

  // Process encounter hook for admin users
  const { processEncounter, isLoading: isProcessingEncounter } = useProcessEncounter();
  const { rerunWithDecisionLog, isLoading: isRerunningWithDecisionLog } = useRerunWithDecisionLog();
  const [isDecisionLogRerunDialogOpen, setIsDecisionLogRerunDialogOpen] = React.useState<boolean>(false);
  const { rejectResultMutation } = useWriteBackActions();
  const { mutateAsync: rejectEncounter, isPending: isRejecting } = rejectResultMutation;
  const { mutateAsync: acceptResult, isPending: isAccepting } = useAcceptClassificationResult();

  const handleAcceptResult = useCallback(
    async (reason: string) => {
      if (!focusedClassificationResultId) return;
      try {
        await acceptResult({
          inboundClassificationResultId: focusedClassificationResultId,
          reason,
        });
        setIsAcceptDialogOpen(false);
      } catch {
        // Error toast handled in the mutation; keep the dialog open so the user can retry.
      }
    },
    [focusedClassificationResultId, acceptResult],
  );

  // Convert fromDate to UTC ISO day start for query
  const dateFrom = fromDate ? dayjs(fromDate).startOf("day").toDate().toISOString() : undefined;

  // Next unwritten encounter hook for navigation
  const { refetchWithExcludeIds, isLoading: isLoadingNext } = useNextUnwrittenEncounter({
    enabled: !!encounterId,
    customerIds: filterCustomerIds,
    fromDate: dateFrom,
    excludeIds: encounterId ? [encounterId, ...skipEncounterIds] : [...skipEncounterIds],
  });

  // Auto-fetch next encounter when no encounterId in URL (worklist mode)
  const {
    data: autoFetchData,
    isLoading: isAutoFetchLoading,
    error: autoFetchError,
    refetch: autoFetchRefetch,
  } = useNextUnwrittenEncounter({
    enabled: !encounterId,
    customerIds: filterCustomerIds,
    fromDate: dateFrom,
    excludeIds: skipEncounterIds,
  });

  // Auto-redirect when next encounter is found in worklist mode
  useEffect(() => {
    if (!encounterId && autoFetchData?.encounterId) {
      navigate(`${ROUTES.unwrittenEncounterReview.route}/${autoFetchData.encounterId}`);
    }
  }, [encounterId, autoFetchData, navigate]);

  // if the encounterId changes, re-set our state
  useEffect(() => {
    setFocusedClassificationResultId(null);
    // Note: hiddenReviewerIds persists across encounters via localStorage in useReviewersFilter hook
  }, [encounterId]);

  const handleSkip = useCallback(async () => {
    if (!encounterId) return;

    // Add current encounter ID to skip list
    addSkipEncounterId(encounterId);

    // Refetch with updated excludeIds (including the newly added skip ID)
    const nextEncounter = await refetchWithExcludeIds([encounterId, ...skipEncounterIds]);

    if (nextEncounter?.encounterId) {
      navigate(`${ROUTES.unwrittenEncounterReview.route}/${nextEncounter.encounterId}`);
    } else {
      navigate(ROUTES.unwrittenEncounterReview.route);
    }
  }, [encounterId, addSkipEncounterId, skipEncounterIds, refetchWithExcludeIds, navigate]);

  const handleNavigateToNext = useCallback(async () => {
    if (!encounterId) return;

    // Refetch with updated excludeIds (including the current encounter ID)
    const nextEncounter = await refetchWithExcludeIds([encounterId, ...skipEncounterIds]);

    if (nextEncounter?.encounterId) {
      navigate(`${ROUTES.unwrittenEncounterReview.route}/${nextEncounter.encounterId}`);
    } else {
      navigate(ROUTES.unwrittenEncounterReview.route);
    }
  }, [encounterId, skipEncounterIds, refetchWithExcludeIds, navigate]);

  const handleProcessEncounter = useCallback(async () => {
    if (!encounterId) return;

    try {
      await processEncounter(encounterId);
      toast.success("Encounter submitted for processing", {
        description: "The encounter has been successfully submitted.",
      });
    } catch (error) {
      console.error("Error processing encounter:", error);
      toast.error("Failed to process encounter", {
        description: error instanceof Error ? error.message : "An unknown error occurred.",
      });
    }
  }, [encounterId, processEncounter]);

  const handleRerunWithDecisionLog = useCallback(async () => {
    if (!encounterId) return;

    try {
      await rerunWithDecisionLog(encounterId);
      setIsDecisionLogRerunDialogOpen(false);
      toast.success("Re-run started with the decision log on", {
        description: "The log appears under View decision log once the run finishes.",
      });
    } catch (error) {
      console.error("Error re-running encounter with decision log:", error);
      toast.error("Failed to start the re-run", {
        description: error instanceof Error ? error.message : "An unknown error occurred.",
      });
    }
  }, [encounterId, rerunWithDecisionLog]);

  const handleRejectEncounter = useCallback(async () => {
    if (!encounterId) return;

    try {
      await rejectEncounter({ encounter_id: encounterId });
      setIsRejectDialogOpen(false);
      await queryClient.invalidateQueries({
        queryKey: ["patient_encounters", encounterId],
      });
      await queryClient.invalidateQueries({
        queryKey: classificationResultQueryKey(focusedClassificationResultId),
      });

      handleNavigateToNext();
    } catch (error) {
      console.error("Error rejecting encounter:", error);
      toast.error("Failed to reject encounter", {
        description: error instanceof Error ? error.message : "An unknown error occurred.",
      });
    }
  }, [encounterId, focusedClassificationResultId, queryClient, rejectEncounter, handleNavigateToNext]);

  const handleVoidReview = useCallback(
    async (humanReviewId: string) => {
      if (!encounterId) return;
      setIsVoidingReview(true);
      try {
        await voidEncounterReview({
          encounter_id: encounterId,
          human_review_id: humanReviewId,
        });
        toast.success("Review voided", {
          description: "The review will be excluded from write-back and listings.",
        });
        await queryClient.invalidateQueries({
          queryKey: ["patient_encounters", encounterId],
        });
        await queryClient.invalidateQueries({
          queryKey: ["human-review", humanReviewId],
        });
      } catch (error) {
        console.error("Error voiding review", error);
        toast.error(error instanceof Error ? error.message : "An unknown error occurred.");
      } finally {
        setIsVoidingReview(false);
      }
    },
    [encounterId, queryClient],
  );

  const handleVoidUnvoidConfirm = useCallback(async () => {
    if (!voidAction || !encounterId) return;

    const payload = {
      encounter_id: encounterId,
      human_review_id: voidAction.humanReviewId,
    };

    setIsVoidingReview(true);
    try {
      await unvoidEncounterReview(payload);
      toast.success("Review restored", {
        description: "The review has been unvoided.",
      });
      setVoidAction(null);
      await queryClient.invalidateQueries({
        queryKey: ["patient_encounters", encounterId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["human-review", voidAction.humanReviewId],
      });
    } catch (error) {
      console.error("Error unvoiding review", error);
      toast.error(error instanceof Error ? error.message : "An unknown error occurred.");
    } finally {
      setIsVoidingReview(false);
    }
  }, [voidAction, encounterId, queryClient]);

  // Fetch encounter data from the view
  const {
    data: encounterData,
    isLoading: isLoadingEncounter,
    error: encounterError,
  } = useQuery({
    queryKey: ["patient_encounters", encounterId],
    queryFn: async () => {
      if (!encounterId) throw new Error("Encounter ID is required");

      const { data, error } = await supabase
        .from("patient_encounters")
        .select(
          `*,
          customer:customers(label, provider_id),
          classificationResults:inbound_classification_results(
            *,
            writeBack:outbound_result_write_backs!outbound_result_write_backs_inbound_classification_result_id_fk(
              id, status, sent_back_at, written_to_s3_at, error_message, s3_key, supersedes, human_review_id
            )
          ),
          humanReviews:encounter_human_review(*)
          `,
        )
        .eq("id", encounterId)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error("Encounter not found");

      return {
        encounterId: data.id,
        patientId: data.patient_id,
        customerId: data.customer_id,
        customerProviderId: data.customer?.provider_id ?? null,
        specialty: data.specialty,
        observationDateTime: data.admit_datetime,
        patientAge: data.patient_age,
        patientSex: data.patient_sex,
        classificationResults: data.classificationResults,
        humanReviews: data.humanReviews,
        customer: data.customer,
        dischargeDatetime: data.discharge_datetime,
      };
    },
    enabled: !!encounterId,
  });
  const { isFlashing } = useCustomerChangeAlert(encounterData?.customerProviderId ?? null);

  // Only show complete reviews; exclude pending
  const humanReviewsComplete = React.useMemo(() => {
    if (!encounterData?.humanReviews) return [];
    return encounterData.humanReviews.filter((review) => review.status === "complete");
  }, [encounterData?.humanReviews]);

  // Extract unique reviewer IDs from complete human reviews only
  const reviewerIds = React.useMemo(() => {
    if (!humanReviewsComplete.length) return [];
    const uniqueIds = new Set<string>();
    humanReviewsComplete.forEach((review) => {
      if (review.reviewer_id) {
        uniqueIds.add(review.reviewer_id);
      }
    });
    return Array.from(uniqueIds);
  }, [humanReviewsComplete]);

  // Fetch reviewer profiles
  const { data: reviewerProfiles } = useUserProfiles(reviewerIds);

  // Create memoized list of reviewers with their info
  const reviewersList = React.useMemo(() => {
    if (!reviewerProfiles || reviewerIds.length === 0) return [];
    return reviewerIds
      .map((reviewerId) => {
        const profile = reviewerProfiles[reviewerId];
        return {
          id: reviewerId,
          displayName: profile?.display_name ?? "Unknown Reviewer",
          displayColor: profile?.display_color || "#6366f1",
        };
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [reviewerIds, reviewerProfiles]);

  const sortedClassificationResults = React.useMemo(() => {
    if (!encounterData?.classificationResults) return [];
    return [...encounterData.classificationResults].sort(
      (a, b) => dayjs(b.classified_at).valueOf() - dayjs(a.classified_at).valueOf(),
    );
  }, [encounterData?.classificationResults]);

  const writeBackByResultId = React.useMemo(() => {
    const delivery = readDeliveryChain(
      sortedClassificationResults.flatMap((result) =>
        (result.writeBack ?? []).map((writeBack) => ({ ...writeBack, resultId: result.id })),
      ),
    );

    return new Map(
      sortedClassificationResults.map((result) => [
        result.id,
        resolveOwnWriteBack(delivery, (row) => row.resultId === result.id && row.human_review_id === null),
      ]),
    );
  }, [sortedClassificationResults]);

  const opcsLabel = React.useMemo(
    () =>
      encounterData
        ? getOpcsVersionLabel(getOpcsVersionForDate(encounterData.dischargeDatetime ?? undefined))
        : undefined,
    [encounterData?.dischargeDatetime],
  );

  const isGoldenDatasetSupportedForEncounter = isGoldenDatasetSupported(encounterData?.specialty);

  // Filter complete human reviews based on hidden reviewers
  const filteredHumanReviews = React.useMemo(() => {
    if (!humanReviewsComplete.length) return [];
    return humanReviewsComplete.filter((review) => !hiddenReviewerIds.has(review.reviewer_id));
  }, [humanReviewsComplete, hiddenReviewerIds]);

  const outcomeColor = (outcome?: string) => {
    if (!outcome) return "bg-gray-100 text-gray-800";
    if (outcome === "success" || outcome === "approved") return "bg-green-100 text-green-800";
    if (outcome === "error" || outcome === "rejected" || outcome === "fail" || outcome === "failed")
      return "bg-red-100 text-red-800";
    return "bg-gray-100 text-gray-800";
  };

  useEffect(() => {
    // Default to focus the most recent classification result regardless of status
    if (!focusedClassificationResultId && encounterData && encounterData.classificationResults.length > 0) {
      const mostRecentResult = [...encounterData.classificationResults].sort(
        (a, b) => dayjs(b.classified_at).valueOf() - dayjs(a.classified_at).valueOf(),
      )[0];
      setFocusedClassificationResultId(mostRecentResult?.id ?? null);
    }
  }, [encounterData?.classificationResults, focusedClassificationResultId]);

  // fetch the specific classification result for the codes
  const { data: classificationResultData } = useQuery({
    queryKey: classificationResultQueryKey(focusedClassificationResultId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("inbound_classification_results")
        .select(
          `
          *,
          procedures:inbound_classification_result_procedures(*),
          diagnoses:inbound_classification_result_diagnosis(*),
          inbound_endoscopy_specific_results(was_specimen_or_histology_taken, primary_diagnosis_identified)
          `,
        )
        .eq("id", focusedClassificationResultId!)
        .single();
      if (error) throw error;
      if (!data) throw new Error("Classification result not found");

      // fetch the code descriptions for the procedures and diagnoses
      const { data: codeDescriptions } = await supabase
        .from("opcs4_codes")
        .select("code, description")
        .in(
          "code",
          data.procedures.map((procedure) => procedure.opcs_4_code),
        );
      const { data: diagnosisDescriptions } = await supabase
        .from("icd10_codes")
        .select("code, description")
        .in(
          "code",
          data.diagnoses.map((diagnosis) => diagnosis.icd_10_code),
        );

      const procedures = data.procedures
        .slice()
        .sort((a, b) => a.order_index - b.order_index)
        .map((procedure) => ({
          code: procedure.opcs_4_code,
          description: codeDescriptions?.find((code) => code.code === procedure.opcs_4_code)?.description || "",
          orderIndex: procedure.order_index,
          aiFlagText: procedure.ai_flag_text,
        }));

      const diagnoses = data.diagnoses
        .slice()
        .sort((a, b) => a.order_index - b.order_index)
        .map((diagnosis) => ({
          code: diagnosis.icd_10_code,
          description: diagnosisDescriptions?.find((code) => code.code === diagnosis.icd_10_code)?.description || "",
          orderIndex: diagnosis.order_index,
          isHistoricCode: diagnosis.is_historic_code,
        }));

      return {
        ...data,
        procedures,
        diagnoses,
        wasSpecimenOrHistologyTaken: data.inbound_endoscopy_specific_results?.was_specimen_or_histology_taken ?? false,
        primaryDiagnosisIdentified: data.inbound_endoscopy_specific_results?.primary_diagnosis_identified ?? null,
      };
    },
    enabled: !!focusedClassificationResultId,
  });

  const historicCodes = React.useMemo(
    () => historicCodeSet(classificationResultData?.diagnoses),
    [classificationResultData?.diagnoses],
  );

  // Fetch the files for the focused classification result
  const {
    data: files,
    isLoading: isLoadingFiles,
    error: filesError,
  } = useProcessedFiles(focusedClassificationResultId ?? undefined);

  const { data: evidenceData } = useDiagnosisEvidence(focusedClassificationResultId ?? undefined);
  const { data: unmatchedLeadTerms } = useUnmatchedLeadTerms(focusedClassificationResultId ?? undefined);
  const { data: indications } = useIndicationVerdicts(encounterId);
  const {
    timelinePanel,
    allHighlights,
    activeFileIdentifier,
    scrollTrigger,
    evidenceByCode,
    evidenceHighlight,
    handleOpenTimeline,
    handleCloseTimeline,
    handleToggleHighlight,
    handleTimelineEvidenceClick,
  } = useEvidenceHighlighting(evidenceData, unmatchedLeadTerms);

  const droppedCodes = React.useMemo(() => {
    if (!evidenceData || !classificationResultData) return [];
    const activeCodes = new Set(classificationResultData.diagnoses.map((d) => d.code));
    return evidenceData.filter((row) => !activeCodes.has(row.icd10Code));
  }, [evidenceData, classificationResultData]);

  // Fetch the first visible human review's codes so we can highlight removed AI codes
  const firstHumanReviewId = pickFirstNonVoidedReviewId(filteredHumanReviews);

  const { data: firstHumanReviewCodes } = useQuery({
    queryKey: ["human-review-codes", firstHumanReviewId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("encounter_human_review")
        .select(
          `procedures:encounter_human_review_procedure_code(code, order_index),
         diagnoses:encounter_human_review_diagnosis_code(code, order_index)`,
        )
        .eq("id", firstHumanReviewId!)
        .single();
      if (error) throw error;
      return {
        procedures: data.procedures
          .slice()
          .sort((a, b) => a.order_index - b.order_index)
          .map((p) => ({ code: p.code, description: null, orderIndex: p.order_index })),
        diagnoses: data.diagnoses
          .slice()
          .sort((a, b) => a.order_index - b.order_index)
          .map((d) => ({ code: d.code, description: null, orderIndex: d.order_index })),
      };
    },
    enabled: !!firstHumanReviewId,
  });

  const codesRemovedByHuman = React.useMemo(() => {
    if (!firstHumanReviewCodes || !classificationResultData) return undefined;

    const removedProcs = findRemovedCodes(classificationResultData.procedures, firstHumanReviewCodes.procedures);
    const removedDiags = findRemovedCodes(classificationResultData.diagnoses, firstHumanReviewCodes.diagnoses);

    const removedSet = new Set<string>();
    for (const code of [...removedProcs, ...removedDiags]) {
      removedSet.add(code.code);
    }

    return removedSet.size > 0 ? removedSet : undefined;
  }, [firstHumanReviewCodes, classificationResultData]);

  const evidenceCallbacks = React.useMemo(
    () =>
      evidenceData
        ? {
            onOpenTimeline: handleOpenTimeline,
            onToggleHighlight: handleToggleHighlight,
            hasTimeline: (code: string) => evidenceByCode.has(code),
            hasQuotes: (code: string) => {
              const entry = evidenceByCode.get(code);
              return entry ? countTimelineQuotes(entry.timeline) > 0 : false;
            },
            focusedCode: evidenceHighlight.focusedCode,
            activeTimelineCode: timelinePanel.activeCode,
          }
        : undefined,
    [
      evidenceData,
      evidenceByCode,
      evidenceHighlight.focusedCode,
      timelinePanel.activeCode,
      handleOpenTimeline,
      handleToggleHighlight,
    ],
  );

  // Worklist mode: no encounterId in URL
  if (!encounterId) {
    return (
      <div className="space-y-4 p-6">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-3xl font-bold">Approved Results</h1>
          <ApprovedResultsFilterPopover
            customerIds={filterCustomerIds}
            fromDate={fromDate}
            onApply={setFilters}
            onClear={clearFilters}
            isCustomised={isCustomised}
          />
        </div>
        {isAutoFetchLoading && <div>Loading...</div>}
        {autoFetchError && <div>Error: {autoFetchError.message}</div>}
        {!isAutoFetchLoading && !autoFetchError && !autoFetchData && (
          <div className="space-y-2">
            <div>
              All approved encounters have been processed. The system will automatically check for new encounters and
              redirect you when one is ready for review.
            </div>
            <Button onClick={() => autoFetchRefetch()} variant="outline">
              Refresh
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (encounterError) {
    return (
      <div className="space-y-4 p-6">
        <div className="font-medium">Error: {encounterError.message}</div>
        {encounterId && (
          <div className="text-sm text-muted-foreground">
            Encounter ID: <code className="rounded bg-muted px-1">{encounterId}</code>
            {" — "}
            This ID may not exist in the connected database or you may not have access.
          </div>
        )}
      </div>
    );
  }

  if (isLoadingEncounter) {
    return (
      <div className="space-y-4 p-6">
        <div>Loading encounter data...</div>
      </div>
    );
  }

  if (!encounterData) {
    return (
      <div className="space-y-4 p-6">
        <div className="font-medium">Encounter not found</div>
        {encounterId && (
          <div className="text-sm text-muted-foreground mt-1">
            Encounter ID: <code className="rounded bg-muted px-1">{encounterId}</code>
            {" — "}
            This ID may not exist in the connected database or you may not have access.
          </div>
        )}
      </div>
    );
  }

  const headerSlotEl =
    pageHeaderSlot?.slotReady && pageHeaderSlot.headerSlotRef.current ? pageHeaderSlot.headerSlotRef.current : null;

  const renderHeader = (inHeader = false) => (
    <div
      className={`flex items-center justify-between w-full ${inHeader ? "text-primary-foreground" : "text-foreground"}`}
    >
      <div className="flex items-center gap-2 sm:gap-3">
        <TrustRulesButton
          customerId={encounterData.customerId}
          customerProviderId={encounterData.customerProviderId}
          customerLabel={encounterData.customer?.label}
          inHeader={inHeader}
        />
        <div className="text-sm font-medium">Encounter Review</div>
        <ApprovedResultsFilterPopover
          inHeader={inHeader}
          customerIds={filterCustomerIds}
          fromDate={fromDate}
          onApply={(filters) => {
            setFilters(filters);
            // Redirect to base route to re-fetch with new filters
            navigate(ROUTES.unwrittenEncounterReview.route);
          }}
          onClear={() => {
            clearFilters();
            navigate(ROUTES.unwrittenEncounterReview.route);
          }}
          isCustomised={isCustomised}
        />
        <Button
          variant={inHeader ? "ghostOnPrimary" : "ghost"}
          onClick={handleSkip}
          disabled={isLoadingNext || !encounterId}
          size="sm"
        >
          <ArrowRight className="h-4 w-4 mr-1" />
          <span className="hidden sm:inline">Skip</span>
        </Button>
        <Button
          variant={inHeader ? "ghostOnPrimary" : "ghost"}
          onClick={clearSkipEncounterIds}
          disabled={skipEncounterIds.length === 0}
          size="sm"
          className="h-8 w-8 p-0"
          title="Clear skipped encounters list"
          aria-label="Clear skipped encounters list"
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
        {role === USER_ROLES.admin && encounterId && (
          <ReprocessSplitButton
            variant={inHeader ? "ghostOnPrimary" : "ghost"}
            isBusy={isProcessingEncounter || isRerunningWithDecisionLog}
            onReprocess={handleProcessEncounter}
            onRerunWithDecisionLog={
              encounterData?.specialty === "endoscopy" ? () => setIsDecisionLogRerunDialogOpen(true) : undefined
            }
          />
        )}
        <OpenRawFilesButton encounterId={encounterId} variant={inHeader ? "ghostOnPrimary" : "ghost"} newTab />
        <EncounterToolsMenu
          variant={inHeader ? "ghostOnPrimary" : "ghost"}
          decisionLogUrl={
            role === USER_ROLES.admin &&
            encounterId &&
            encounterData?.specialty === "endoscopy" &&
            encounterData.customerProviderId
              ? decisionLogFolderUrl(encounterData.customerProviderId, encounterId)
              : undefined
          }
          onOpenInvestigator={
            (role === USER_ROLES.admin || role === USER_ROLES.clinical_coder_admin) &&
            encounterId &&
            classificationResultData
              ? () => setIsInvestigatorOpen(true)
              : undefined
          }
        />
        {role === USER_ROLES.admin && encounterId && (
          <Button
            variant="ghost"
            onClick={() => setIsRejectDialogOpen(true)}
            disabled={isRejecting || !encounterId}
            size="sm"
            className="text-red-300 hover:bg-red-900/50 hover:text-red-100"
          >
            {isRejecting ? "Rejecting..." : "Reject Encounter"}
          </Button>
        )}
        {encounterData?.humanReviews && encounterData.humanReviews.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant={inHeader ? "ghostOnPrimary" : "ghost"} size="sm">
                <Filter className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Filter Reviewers</span>
                {hiddenReviewerIds.size > 0 && (
                  <Badge variant="secondary" className="ml-2 h-5 min-w-[20px] px-1.5">
                    {hiddenReviewerIds.size}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Show/Hide Reviewers</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {reviewersList.length === 0 ? (
                <div className="px-2 py-1.5 text-sm text-muted-foreground">No reviewers found</div>
              ) : (
                reviewersList.map((reviewer) => (
                  <DropdownMenuCheckboxItem
                    key={reviewer.id}
                    checked={!hiddenReviewerIds.has(reviewer.id)}
                    onCheckedChange={() => toggleReviewerVisibility(reviewer.id)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full" style={{ backgroundColor: reviewer.displayColor }} />
                      <span>{reviewer.displayName}</span>
                    </div>
                  </DropdownMenuCheckboxItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );

  if (sortedClassificationResults.length === 0) {
    return (
      <div
        className={`h-full min-h-0 flex flex-col ${isFlashing ? "animate-customer-flash" : ""}`}
        style={{
          backgroundColor: getReviewPageBackground(encounterData.customerProviderId ?? null),
        }}
      >
        {headerSlotEl ? (
          createPortal(renderHeader(true), headerSlotEl)
        ) : (
          <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-2 sm:px-4 py-1">{renderHeader()}</div>
        )}
        <div className="flex flex-1 items-center justify-center p-6">
          <div className="text-center space-y-3 max-w-md">
            <p className="text-muted-foreground">
              No classification results for this encounter. Use &apos;Reprocess Encounter&apos; to submit the encounter
              for classification.
            </p>
            <div className="text-sm text-muted-foreground flex flex-wrap justify-center gap-x-4 gap-y-1">
              <span>Sex: {encounterData.patientSex}</span>
              <span>Age: {encounterData.patientAge}</span>
              <span>Obx date: {dayjs(encounterData.observationDateTime).tz().format("DD/MM/YY HH:mm")}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!classificationResultData) {
    return (
      <div
        className={`h-full min-h-0 flex flex-col ${isFlashing ? "animate-customer-flash" : ""}`}
        style={{
          backgroundColor: getReviewPageBackground(encounterData.customerProviderId ?? null),
        }}
      >
        {headerSlotEl ? (
          createPortal(renderHeader(true), headerSlotEl)
        ) : (
          <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-2 sm:px-4 py-1">{renderHeader()}</div>
        )}
        <div className="flex flex-1 items-center justify-center p-6">
          <div>Loading classification result...</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`h-full min-h-0 flex flex-col ${isFlashing ? "animate-customer-flash" : ""}`}
      style={{
        backgroundColor: getReviewPageBackground(encounterData.customerProviderId ?? null),
      }}
    >
      {headerSlotEl ? (
        createPortal(renderHeader(true), headerSlotEl)
      ) : (
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-2 sm:px-4 py-1">{renderHeader()}</div>
      )}
      <div className="flex flex-1 overflow-hidden bg-white">
        <div
          className="border-r border-gray-200 overflow-y-auto bg-white p-4 min-w-0"
          style={{ width: `${leftColumn.width}%`, minWidth: "240px" }}
        >
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Sex: {encounterData.patientSex}</span>
            <span className="text-sm text-gray-500">Age: {encounterData.patientAge}</span>
            <span className="text-sm text-gray-500">
              Obx date: {dayjs(encounterData.observationDateTime).tz().format("DD/MM/YY HH:mm")}
            </span>
            <TooltipProvider>
              {classificationResultData.wasSpecimenOrHistologyTaken ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-sm text-gray-500">🔬</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Specimen or histology was taken</p>
                  </TooltipContent>
                </Tooltip>
              ) : null}
              {classificationResultData.ai_flag_for_review ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-sm text-gray-500">🤖🚩</span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[min(90vw,30rem)] break-words whitespace-pre-line">
                    {(() => {
                      const aiFlagText = classificationResultData.ai_flag_for_review_text || "Flagged by AI for review";
                      return role === USER_ROLES.admin && aiFlagText ? aiFlagText : "Flagged by AI for review";
                    })()}
                  </TooltipContent>
                </Tooltip>
              ) : null}
              {isManuallyApproved(classificationResultData.human_accepted) ? (
                <span className="text-sm text-gray-500">🧠</span>
              ) : null}
              {classificationResultData.is_follow_up ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-sm text-gray-500">🔄</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Follow-up encounter</p>
                  </TooltipContent>
                </Tooltip>
              ) : null}
            </TooltipProvider>
          </div>
          {firstHumanReviewCodes && classificationResultData.outcome !== "error" ? (
            <ScoreDisplay
              aiDiagnoses={classificationResultData.diagnoses.map((d) => d.code)}
              aiProcedures={classificationResultData.procedures.map((p) => p.code)}
              aiFlaggedForReview={classificationResultData.ai_flag_for_review}
              humanDiagnoses={firstHumanReviewCodes.diagnoses.map((d) => d.code)}
              humanProcedures={firstHumanReviewCodes.procedures.map((p) => p.code)}
            />
          ) : null}
          <ProcessedFilesTabs
            files={files}
            isLoading={isLoadingFiles}
            error={filesError}
            allHighlights={allHighlights}
            activeFileIdentifier={activeFileIdentifier}
            scrollTrigger={scrollTrigger}
            indications={indications}
          />
        </div>

        <ResizeHandle
          onMouseDown={leftColumn.handleMouseDown}
          isDragging={leftColumn.isDragging}
          ariaLabel="Resize columns"
        />

        <div
          className="flex flex-col p-3 space-y-3 overflow-y-auto overflow-x-hidden shrink-0 min-w-0"
          style={{ width: `${aiColumn.width}px`, minWidth: "280px" }}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="relative">
              <select
                className="w-full text-xs border border-gray-300 rounded-md px-2 py-1 bg-white shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                value={focusedClassificationResultId ?? ""}
                onChange={(e) => setFocusedClassificationResultId(e.target.value)}
              >
                {sortedClassificationResults.map((result) => (
                  <option key={result.id} value={result.id} className={outcomeColor(result.outcome)}>
                    {dayjs(result.classified_at).tz().format("DD/MM HH:mm")} — {result.outcome ?? "pending"}{" "}
                    {writeBackLabel(writeBackByResultId.get(result.id))}
                  </option>
                ))}
              </select>
            </div>
            {classificationResultData?.run_id && role === USER_ROLES.admin && (
              <ViewRunDropdown
                mastraRunUrl={getMastraWorkflowRunUrl(encounterData?.specialty, classificationResultData.run_id)}
                langfuseTraceId={classificationResultData.langfuse_trace_id}
                encounterId={encounterData?.encounterId}
                onApprove={() => setIsAcceptDialogOpen(true)}
                isApproved={isManuallyApproved(classificationResultData.human_accepted)}
              />
            )}
          </div>
          <div className="rounded-md border border-gray-200 bg-white space-y-2 overflow-x-hidden">
            <AiResultsColumn
              encounterId={encounterData.encounterId}
              inboundClassificationResultId={focusedClassificationResultId}
              procedures={classificationResultData.procedures}
              diagnoses={classificationResultData.diagnoses}
              primaryDiagnosisIdentified={classificationResultData.primaryDiagnosisIdentified}
              customerLabel={encounterData.customer.label}
              onSubmitSuccess={handleNavigateToNext}
              evidenceCallbacks={evidenceCallbacks}
              droppedCodes={droppedCodes}
              unmatchedLeadTerms={unmatchedLeadTerms}
              codesRemovedByHuman={codesRemovedByHuman}
              opcsLabel={opcsLabel}
              isGoldenDatasetSupported={isGoldenDatasetSupportedForEncounter}
              specialty={encounterData.specialty}
              patientAge={encounterData.patientAge}
              patientSex={encounterData.patientSex}
              historicCodes={historicCodes}
            />
          </div>
        </div>

        <ResizeHandle
          onMouseDown={aiColumn.handleMouseDown}
          isDragging={aiColumn.isDragging}
          ariaLabel="Resize AI column"
          shadow={false}
        />

        <div className="flex-1 min-w-0 overflow-x-auto overflow-y-auto">
          {filteredHumanReviews.length === 0 ? (
            <div className="flex items-center justify-center h-full p-4">
              <div className="text-center text-muted-foreground">
                {humanReviewsComplete.length === 0 ? "No reviews available" : "All reviewers are hidden"}
              </div>
            </div>
          ) : (
            <div className="flex flex-nowrap gap-4 lg:gap-5 p-4 min-w-max">
              {filteredHumanReviews.map((humanReview) => (
                <div key={humanReview.id} className="w-[360px]">
                  <HumanReviewColumn
                    reviewId={humanReview.id}
                    encounterId={encounterData.encounterId}
                    customer={encounterData.customer}
                    onSubmitSuccess={handleNavigateToNext}
                    aiCodesForComparison={{
                      procedures: classificationResultData.procedures,
                      diagnoses: classificationResultData.diagnoses,
                    }}
                    onRequestVoidReview={canVoidUnvoidReview ? (reviewId) => handleVoidReview(reviewId) : undefined}
                    onRequestUnvoidReview={
                      canVoidUnvoidReview
                        ? (reviewId) => setVoidAction({ type: "unvoid", humanReviewId: reviewId })
                        : undefined
                    }
                    canVoidUnvoidReview={canVoidUnvoidReview}
                    isVoided={isReviewVoided(humanReview)}
                    opcsLabel={opcsLabel}
                    isGoldenDatasetSupported={isGoldenDatasetSupportedForEncounter}
                    specialty={encounterData.specialty}
                    patientAge={encounterData.patientAge}
                    patientSex={encounterData.patientSex}
                    historicCodes={historicCodes}
                    primaryDiagnosisIdentified={classificationResultData.primaryDiagnosisIdentified}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        {timelinePanel.activeCode && evidenceByCode.get(timelinePanel.activeCode) && (
          <div className="w-[360px] min-w-[360px] border-l border-gray-200 overflow-y-auto">
            <DiagnosisTimeline
              code={timelinePanel.activeCode}
              description={evidenceByCode.get(timelinePanel.activeCode)!.description}
              timeline={evidenceByCode.get(timelinePanel.activeCode)!.timeline}
              onClose={handleCloseTimeline}
              onEvidenceClick={handleTimelineEvidenceClick}
            />
          </div>
        )}
      </div>

      <ConfirmationDialog
        isOpen={isDecisionLogRerunDialogOpen}
        onClose={() => setIsDecisionLogRerunDialogOpen(false)}
        onConfirm={handleRerunWithDecisionLog}
        title="Re-run with decision log"
        description="This classifies the encounter again with the decision log on and makes a new result. It uses real model calls, so it costs money."
        confirmText="Re-run"
        isLoading={isRerunningWithDecisionLog}
      />

      <ConfirmationDialog
        isOpen={isRejectDialogOpen}
        onClose={() => setIsRejectDialogOpen(false)}
        onConfirm={handleRejectEncounter}
        title="Reject Encounter"
        description="Are you sure you want to reject this encounter? This action cannot be undone."
        confirmText="Reject"
        variant="destructive"
        isLoading={isRejecting}
      />

      <ConfirmationDialog
        isOpen={voidAction !== null}
        onClose={() => setVoidAction(null)}
        onConfirm={handleVoidUnvoidConfirm}
        title="Restore review"
        description="Restore this review?"
        confirmText="Restore"
        variant="destructive"
        isLoading={isVoidingReview}
      />

      <AcceptResultDialog
        isOpen={isAcceptDialogOpen}
        onClose={() => setIsAcceptDialogOpen(false)}
        onSubmit={handleAcceptResult}
        isLoading={isAccepting}
        initialReason={classificationResultData?.human_accepted}
      />

      <InvestigatorPanel
        encounterId={encounterId ?? null}
        open={isInvestigatorOpen}
        onOpenChange={setIsInvestigatorOpen}
      />
    </div>
  );
};

export default UnwrittenEncounterReview;
